import {
  useEffect,
  useRef,
  type CSSProperties,
  type PropsWithChildren,
  type ReactNode,
} from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import './modal.css';

interface ModalProps extends PropsWithChildren {
  /** Rendered inside the heading, so a node must still read as the title. */
  title: ReactNode;
  onClose: () => void;
  labelledBy?: string;
  className?: string;
  backdropClassName?: string;
  backdropStyle?: CSSProperties;
}

export function Modal({
  title,
  onClose,
  labelledBy = 'modal-title',
  className = '',
  backdropClassName = '',
  backdropStyle,
  children,
}: ModalProps) {
  const { t } = useTranslation();
  const onCloseRef = useRef(onClose);
  const dialogRef = useRef<HTMLElement>(null);

  onCloseRef.current = onClose;

  useEffect(() => {
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = dialogRef.current;
    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    const getFocusable = (): HTMLElement[] =>
      dialog
        ? Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector))
        : [];

    (getFocusable()[0] ?? dialog)?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !dialog) return;

      const focusable = getFocusable();
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !dialog.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, []);

  return (
    <div
      className={`modal__backdrop ${backdropClassName}`.trim()}
      style={backdropStyle}
      role="presentation"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
      onWheel={(event) => { if (event.target === event.currentTarget) event.preventDefault(); }}
      onTouchMove={(event) => { if (event.target === event.currentTarget) event.preventDefault(); }}
    >
      <section
        ref={dialogRef}
        className={`modal ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
      >
        <header className="modal__header">
          <h2 id={labelledBy}>{title}</h2>
          <button className="modal__close" type="button" onClick={onClose} aria-label={t('common.buttons.close')}>×</button>
        </header>
        {children}
      </section>
    </div>
  );
}
